import {
  Button,
  Col,
  Image,
  message,
  Row,
  Space,
  Typography,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import {
  DeleteFilled,
  FilePdfTwoTone,
  PictureTwoTone,
  CheckCircleTwoTone,
  SyncOutlined,
} from '@ant-design/icons';
import ButtonGlobal from '@shared/Button';
import style from './IdCardUpload.module.scss';
import { Action, FileIds, ImperativeHandle, Props, Slot, State, Target } from './types';
import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileAssignment, useFileUpload, useOptionalFormContext, useThumbnailCache, useWarningThrottle } from './hooks';
import { ACCEPT_EXTENSIONS, ACCEPT_MIME, FILE_NAME_MAX_LENGTH, FILE_NAME_PREFIX_LENGTH, FILE_NAME_SUFFIX_LENGTH, MAX_SIZE_MB } from './constants';

const { Dragger } = Upload;

// ============================================================================
// State Management
// ============================================================================
const initialState: State = {
  slots: {},
  ids: {},
  uploading: {},
  progress: {},
  uploadInput: false,
};

function stateReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_SLOT':
      return {
        ...state,
        slots: { ...state.slots, [action.slot]: action.file },
      };
    case 'SET_ID':
      return {
        ...state,
        ids: { ...state.ids, [action.slot]: action.id },
      };
    case 'SET_UPLOADING':
      return {
        ...state,
        uploading: { ...state.uploading, [action.slot]: action.value },
      };
    case 'SET_PROGRESS':
      return {
        ...state,
        progress: { ...state.progress, [action.slot]: action.value },
      };
    case 'SET_UPLOAD_INPUT':
      return {
        ...state,
        uploadInput: action.value,
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

// ============================================================================
// Utility Functions
// ============================================================================
const FileNameUtils = {
  truncate(name?: string): string | undefined {
    if (!name) return undefined;
    if (name.length <= FILE_NAME_MAX_LENGTH) return name;
    
    const prefix = name.slice(0, FILE_NAME_PREFIX_LENGTH);
    const suffix = name.slice(-FILE_NAME_SUFFIX_LENGTH);
    return `${prefix}…${suffix}`;
  },

  isPdf(fileName?: string): boolean {
    return fileName?.toLowerCase().endsWith('.pdf') ?? false;
  },
};

const FileValidation = {
  isValidType(file: File): boolean {
    return (
      ACCEPT_MIME.includes(file.type as any) ||
      /\.(jpg|jpeg|png|pdf)$/i.test(file.name)
    );
  },

  isValidSize(file: File): boolean {
    if (!file.size) return true;
    return file.size / 1024 / 1024 <= MAX_SIZE_MB;
  },

  validate(file: File): { isValid: boolean; errorKey?: 'type' | 'size' } {
    if (!this.isValidType(file)) {
      return { isValid: false, errorKey: 'type' };
    }
    if (!this.isValidSize(file)) {
      return { isValid: false, errorKey: 'size' };
    }
    return { isValid: true };
  },
};

// ============================================================================
// UI Components
// ============================================================================

type FileRowProps = {
  slot: Slot;
  file?: UploadFile;
  thumbnail?: string;
  uploading?: boolean;
  progress?: number;
  hasId: boolean;
  onRemove: () => void;
  onClick: () => void;
  disabled: boolean;
  showSyncIcon: boolean;
  t: any;
};

function FileRow({
  slot,
  file,
  thumbnail,
  uploading,
  progress,
  hasId,
  onRemove,
  onClick,
  disabled,
  showSyncIcon,
  t,
}: FileRowProps) {
  const fileName = file?.name;
  const isPdf = FileNameUtils.isPdf(fileName);

  const labelKey = slot === 'front' 
    ? 'businessCardOrders.uploadIdCardInfo.uploadFront'
    : 'businessCardOrders.uploadIdCardInfo.uploadBack';

  const ariaLabel = slot === 'front'
    ? 'Şəxsiyyət vəsiqəsinin ön hissəsini sil'
    : 'Şəxsiyyət vəsiqəsinin arxa hissəsini sil';

  return (
    <Space.Compact
      block
      size="small"
      role="button"
      tabIndex={0}
      style={{
        justifyContent: 'space-between',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onClick={() => !disabled && onClick()}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          overflow: 'hidden',
          backgroundColor: '#f5f5f5',
          borderRadius: 6,
          padding: '4px 8px',
          border: '1px solid #d9d9d9',
          flex: 1,
        }}
      >
        {thumbnail ? (
          <Image
            src={thumbnail}
            alt={`Şəxsiyyət vəsiqəsinin ${slot === 'front' ? 'ön' : 'arxa'} hissəsinin izlənməsi`}
            height={24}
            width={24}
            onClick={(e) => e.stopPropagation()}
            style={{ objectFit: 'cover' }}
          />
        ) : isPdf ? (
          <FilePdfTwoTone twoToneColor="#eb2f96" />
        ) : (
          <PictureTwoTone twoToneColor="#eb2f96" />
        )}

        <Typography.Text ellipsis style={{ flex: 1 }}>
          {FileNameUtils.truncate(fileName) ?? t(labelKey)}
        </Typography.Text>

        {uploading ? (
          <></>
        ) : hasId ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              marginRight: 8,
            }}
          >
            <CheckCircleTwoTone twoToneColor="#52c41a" />
            <Typography.Text type="success" style={{ whiteSpace: 'nowrap' }}>
              {t('businessCardOrders.uploadIdCardInfo.uploaded')}
            </Typography.Text>
          </span>
        ) : file && showSyncIcon ? (
          <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
            <SyncOutlined style={{ color: '#015CD1' }} />
          </Typography.Text>
        ) : null}

        <Button
          danger
          type="text"
          icon={<DeleteFilled />}
          disabled={uploading || !file}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={ariaLabel}
        />
      </div>
    </Space.Compact>
  );
}

// ============================================================================
// Main Component
// ============================================================================
const IdCardUpload = React.forwardRef<ImperativeHandle, Props>(
  function IdCardUpload({ uploadFile, onIdsChange, externalFiles, optional }, ref) {
    const { t } = useTranslation();
    const rhf = useOptionalFormContext();
    const warnOnce = useWarningThrottle();

    const [state, dispatch] = useReducer(stateReducer, initialState);
    const { slots, ids, uploading, progress, uploadInput } = state;

    const uploadRef = useRef<any>(null);
    const targetSlotRef = useRef<Target>(null);

    const { thumbs, thumbTick, updateThumbFor } = useThumbnailCache();

    // Update thumbnails when slots change
    useEffect(() => {
      updateThumbFor('front', slots.front);
    }, [slots.front, updateThumbFor]);

    useEffect(() => {
      updateThumbFor('back', slots.back);
    }, [slots.back, updateThumbFor]);

    // Handle external files - use ref to track if already processed
    const externalFilesProcessed = useRef(false);
    
    useEffect(() => {
      if (externalFilesProcessed.current || uploadInput) return;
      
      const updates: Partial<FileIds> = {};
      
      if (externalFiles?.front) {
        dispatch({ type: 'SET_SLOT', slot: 'front', file: externalFiles.front });
        updates.front = externalFiles.front.uid;
      }
      
      if (externalFiles?.back) {
        dispatch({ type: 'SET_SLOT', slot: 'back', file: externalFiles.back });
        updates.back = externalFiles.back.uid;
      }
      
      if (Object.keys(updates).length > 0) {
        // Update all IDs at once
        Object.entries(updates).forEach(([slot, id]) => {
          dispatch({ type: 'SET_ID', slot: slot as Slot, id });
        });
        
        // Use ref to get latest state
        const merged = { ...idsRef.current, ...updates };
        onIdsChange?.(merged);
        externalFilesProcessed.current = true;
      }
    }, [externalFiles, uploadInput, onIdsChange]);

    // Use a ref to always have the latest ids
    const idsRef = useRef<FileIds>(ids);
    useEffect(() => {
      idsRef.current = ids;
    }, [ids]);

    const setIds = useCallback(
      (next: Partial<FileIds>) => {
        // First dispatch to update local state
        if ('front' in next) {
          dispatch({ type: 'SET_ID', slot: 'front', id: next.front });
        }
        if ('back' in next) {
          dispatch({ type: 'SET_ID', slot: 'back', id: next.back });
        }

        // Use ref to get latest state and merge
        const merged = { ...idsRef.current, ...next };
        onIdsChange?.(merged);
      },
      [onIdsChange]
    );

    const { doUpload, setProgress } = useFileUpload(uploadFile, dispatch, setIds);
    const assignFiles = useFileAssignment(slots, warnOnce);

    const openDialogFor = useCallback(
      (which: Slot) => {
        if (which === 'back' && !slots.front) {
          message.info(t('businessCardOrders.idcard.backFrontDep'));
          return;
        }
        targetSlotRef.current = which;
        uploadRef.current?.upload?.uploader?.fileInput?.click?.();
      },
      [slots.front]
    );

    const handleRemove = useCallback(
      (slot: Slot) => {
        dispatch({ type: 'SET_SLOT', slot, file: undefined });
        setIds({ [slot]: undefined });
        setProgress(slot, undefined);
      },
      [setIds, setProgress]
    );

    const uploadProps: UploadProps = useMemo(
      () => ({
        multiple: true,
        openFileDialogOnClick: true,
        accept: ACCEPT_EXTENSIONS,
        showUploadList: false,
        beforeUpload(file) {
          const validation = FileValidation.validate(file as File);
          if (!validation.isValid) {
            const errorMessages = {
              type: t('businessCardOrders.idcard.allowList'),
              size: t('businessCardOrders.idcard.maxSize').replace('MAX_SIZE_MB', MAX_SIZE_MB.toString()),
            };
            if (validation.errorKey) {
              warnOnce(validation.errorKey, errorMessages[validation.errorKey]);
            }
            return Upload.LIST_IGNORE;
          }
          return false;
        },
        async onChange(info) {
          const target = targetSlotRef.current;
          targetSlotRef.current = null;

          const currentUids = new Set(
            [slots.front?.uid, slots.back?.uid].filter(Boolean) as string[]
          );

          const newFiles = info.fileList.filter((f) => !currentUids.has(f.uid));
          if (newFiles.length === 0) return;

          const assignments = assignFiles(newFiles, target);
          if (assignments.length === 0) return;

          // Update state
          assignments.forEach((a) => {
            dispatch({ type: 'SET_SLOT', slot: a.slot, file: a.file });
          });

          // Upload files
          for (const a of assignments) {
            const fileObj = a.file.originFileObj as File | undefined;
            if (fileObj) {
              doUpload(a.slot, fileObj);
            }
          }
        },
        fileList: [slots.front, slots.back].filter(Boolean) as UploadFile[],
      }),
      [doUpload, slots, assignFiles, warnOnce]
    );

    // Imperative API
    React.useImperativeHandle(
      ref,
      () => ({
        getFiles: () => ({ slots, ids }),
        reset: () => {
          dispatch({ type: 'RESET' });
          externalFilesProcessed.current = false;
          idsRef.current = {};
          onIdsChange?.({});
        },
        setFile: (slot: Slot, file?: UploadFile) => {
          const newId = file?.uid;
          
          // Update slot
          dispatch({ type: 'SET_SLOT', slot, file });
          dispatch({ type: 'SET_ID', slot, id: newId });
          
          // Use ref to get latest state and merge
          const updatedIds = { ...idsRef.current, [slot]: newId };
          idsRef.current = updatedIds;
          onIdsChange?.(updatedIds);
        },
      }),
      [slots, onIdsChange]
    );

    const isFrontSelected = Boolean(slots.front);
    const isBackSelected = Boolean(slots.back);
    const isBothSelected = isFrontSelected && isBackSelected;

    return (
      <Row
        style={{
          padding: 20,
          backgroundColor: '#fff',
          border: !optional || isFrontSelected ? '' : '1px solid #FF3B30',
          flexDirection: 'column',
          borderRadius: 12,
        }}
        gutter={[0, 12]}
        wrap
      >
        <Col span={24}>
          <Typography.Text strong style={{ fontSize: 18 }}>
            {t('businessCardOrders.uploadIdCardInfo.title')}
          </Typography.Text>
        </Col>

        <Col span={24}>
          <Row gutter={[20, 0]}>
            <Col span={8}>
              <Dragger
                {...uploadProps}
                ref={uploadRef}
                disabled={isBothSelected}
                style={{ padding: '20px' }}
              >
                <ButtonGlobal
                  disabled={isBothSelected}
                  className={style.uploadBtn}
                  // onClickCapture={() => openDialogFor('front')}
                >
                  {t('businessCardOrders.uploadIdCardInfo.buttonLabel')}
                </ButtonGlobal>
                <p className="ant-upload-text">
                  {t('businessCardOrders.uploadIdCardInfo.or')} <br />{' '}
                  {t('businessCardOrders.uploadIdCardInfo.dropHere')}
                </p>
              </Dragger>
            </Col>

            <Col span={16}>
              <Row
                style={{ flexDirection: 'column', height: '100%' }}
                gutter={[0, 12]}
              >
                <Col>
                  <Typography.Text strong style={{ whiteSpace: 'break-spaces' }}>
                    {t('businessCardOrders.uploadIdCardInfo.uploadFrontAndBack')}
                  </Typography.Text>
                  <p className="ant-upload-text">
                    {t('businessCardOrders.uploadIdCardInfo.accessFileList')}
                  </p>
                  <p className="ant-upload-text">
                    {t('businessCardOrders.uploadIdCardInfo.maxFileSize')}
                  </p>
                </Col>

                <Col>
                  <Space
                    direction="vertical"
                    style={{ width: '100%', gap: 8 }}
                    key={thumbTick}
                  >
                    <FileRow
                      slot="front"
                      file={slots.front}
                      thumbnail={thumbs.current.front}
                      uploading={uploading.front}
                      progress={progress.front}
                      hasId={!!ids.front}
                      onRemove={() => handleRemove('front')}
                      onClick={() => openDialogFor('front')}
                      disabled={!!uploading.front}
                      showSyncIcon={!isBothSelected}
                      t={t}
                    />

                    <FileRow
                      slot="back"
                      file={slots.back}
                      thumbnail={thumbs.current.back}
                      uploading={uploading.back}
                      progress={progress.back}
                      hasId={!!ids.back}
                      onRemove={() => handleRemove('back')}
                      onClick={() => openDialogFor('back')}
                      disabled={!!uploading.back || !slots.front}
                      showSyncIcon={!isBothSelected}
                      t={t}
                    />
                  </Space>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  }
);

export default IdCardUpload;